import '@kerfjs/ui/select/register';

import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { Select } from '@kerfjs/ui/select';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { TextField } from '@kerfjs/ui/text-field';
import { attr, type SafeHtml } from 'kerfjs';

import { VENDOR_TYPES } from '../../../shared/enums.js';
import type { VendorDetailT } from '../../data/api.js';

export const VENDOR_DIALOG_ACTIONS = {
  dialog: attr('data-action', 'vendor-dialog'),
  cancel: attr('data-action', 'vendor-cancel'),
  save: attr('data-action', 'vendor-save'),
} as const;

const typeLabel = (type: string): string => type.charAt(0).toUpperCase() + type.slice(1);

/**
 * Add or edit one vendor: name, type, city, country, and free-text notes
 * (HS2-BBZXKM). Rendered open by the Vendors page while `vendorDialog` is set; a
 * failed save (an empty name or a name another vendor already uses) shows its
 * message in the dialog.
 */
export function VendorDialog({
  vendor,
  error,
}: {
  vendor: VendorDetailT['vendor'] | undefined;
  error: string;
}): SafeHtml {
  const type = vendor?.type ?? '';
  return (
    <DialogSurface
      label={vendor === undefined ? 'New Vendor' : 'Edit Vendor'}
      lightDismiss
      className="vendor-dialog"
      rootAttributes={{
        'data-unsaved-guard': 'this vendor',
        'data-action': VENDOR_DIALOG_ACTIONS.dialog.value,
        'data-auto-open-dialog': '',
      }}
      footer={
        <>
          <Button
            size="default"
            tone="neutral"
            emphasis="plain"
            action={VENDOR_DIALOG_ACTIONS.cancel.value}
            rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            action={VENDOR_DIALOG_ACTIONS.save.value}
            rootAttributes={{ 'data-unsaved-save': '' }}
          >
            {vendor === undefined ? 'Add vendor' : 'Save'}
          </Button>
        </>
      }
    >
      <List gap="m">
        {error === '' ? null : <StateBanner tone="danger" title={error} />}
        <ListInsetControl>
          <TextField name="vendor-name" label="Name" required maxLength={200} autofocus value={vendor?.name ?? ''} />
        </ListInsetControl>
        <ListInsetControl>
          <Select<string>
            name="vendor-type"
            label="Type"
            value={type}
            choices={[
              { value: '', label: 'Not Set' },
              // Keep a stored type the list no longer offers, so editing never silently drops it.
              ...(type !== '' && !(VENDOR_TYPES as readonly string[]).includes(type)
                ? [{ value: type, label: type }]
                : []),
              ...VENDOR_TYPES.map((value) => ({ value, label: typeLabel(value) })),
            ]}
          />
        </ListInsetControl>
        <ListInsetControl>
          <TextField name="vendor-city" label="City" maxLength={120} value={vendor?.city ?? ''} />
        </ListInsetControl>
        <ListInsetControl>
          <TextField name="vendor-country" label="Country" maxLength={120} value={vendor?.country ?? ''} />
        </ListInsetControl>
        <ListInsetControl>
          <TextField
            multiline
            name="vendor-notes"
            label="Notes"
            hint="Payment terms, contacts, reliability — anything the team should know."
            rows={4}
            maxLength={4000}
            value={vendor?.notes ?? ''}
          />
        </ListInsetControl>
      </List>
    </DialogSurface>
  );
}
